import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalFooter,
  Button,
  Label,
  Checkbox,
} from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import useLocationName from '../../../../../../../hooks/useLocationName';

// Component for displaying holding information with translated location
const HoldingItem = ({ holding, controlledVocab }) => {
  const { locationName } = useLocationName(holding.permanentLocationId, controlledVocab);
  const translatedLocation = locationName || 'No Location';

  return (
    <div
      style={{
        padding: '8px 0',
        borderBottom: '1px solid #fecaca',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <div>
        <FormattedMessage
          id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.holdingItem"
          values={{
            index: holding.index,
            location: `[${translatedLocation}]`,
            callNumber: holding.callNumber,
          }}
          defaultMessage="Holding {index} {location} - {callNumber}"
        />
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {/* Items status indicator */}
        {holding.itemCount > 0 && (
          <div style={{
            fontSize: '11px',
            color: holding.itemsAnalysis.canDeleteAll ? '#059669' : '#dc2626',
            backgroundColor: holding.itemsAnalysis.canDeleteAll ? '#ecfdf5' : '#fef2f2',
            padding: '2px 6px',
            borderRadius: '10px',
            border: `1px solid ${holding.itemsAnalysis.canDeleteAll ? '#34d399' : '#fecaca'}`
          }}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.itemsStatus"
              values={{
                available: holding.itemsAnalysis.availableCount,
                unavailable: holding.itemsAnalysis.unavailableCount
              }}
              defaultMessage="{available}A / {unavailable}U"
            />
          </div>
        )}
        {/* Total items count */}
        <div style={{
          fontSize: '12px',
          color: '#dc2626',
          backgroundColor: '#fef2f2',
          padding: '2px 8px',
          borderRadius: '12px',
          border: '1px solid #fecaca'
        }}
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.itemCount"
            values={{ count: holding.itemCount }}
            defaultMessage="{count} items"
          />
        </div>
      </div>
    </div>
  );
};

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  selectedHoldings: PropTypes.arrayOf(PropTypes.string).isRequired,
  holdingsData: PropTypes.arrayOf(PropTypes.object).isRequired,
  itemsData: PropTypes.object.isRequired,
  controlledVocab: PropTypes.object.isRequired,
  isProcessing: PropTypes.bool,
};

function BatchDeleteHoldingsModal({
  isOpen,
  onClose,
  onConfirm,
  selectedHoldings,
  holdingsData,
  itemsData,
  controlledVocab,
  isProcessing = false,
}) {
  // Get selected holdings details with item counts and validation
  const selectedHoldingsDetails = useMemo(() => {
    return selectedHoldings.map(holdingId => {
      const holding = holdingsData.find(h => h.id === holdingId);
      const items = itemsData[holdingId] || [];

      // Calculate items analysis manually to avoid hook in callback
      const availableItems = items.filter(item => item.status?.name === 'Available');
      const unavailableItems = items.filter(item => item.status?.name !== 'Available');

      const itemsAnalysis = {
        availableItems,
        unavailableItems,
        totalItems: items.length,
        availableCount: availableItems.length,
        unavailableCount: unavailableItems.length,
        canDeleteAll: unavailableItems.length === 0,
        hasItems: items.length > 0,
        hasUnavailableItems: unavailableItems.length > 0,
        hasAvailableItems: availableItems.length > 0
      };

      return {
        id: holdingId,
        index: holdingsData.findIndex(h => h.id === holdingId) + 1,
        permanentLocationId: holding?.permanentLocationId,
        callNumber: holding?.callNumber || 'No Call Number',
        itemCount: items.length,
        itemsAnalysis, // Include items analysis for each holding
      };
    });
  }, [selectedHoldings, holdingsData, itemsData, controlledVocab]);

  // Calculate total items that will be deleted
  const totalItemsToDelete = selectedHoldingsDetails.reduce((total, holding) => total + holding.itemCount, 0);

  // Calculate available and unavailable items across all holdings
  const totalAvailableItems = selectedHoldingsDetails.reduce((total, holding) => total + holding.itemsAnalysis.availableCount, 0);
  const totalUnavailableItems = selectedHoldingsDetails.reduce((total, holding) => total + holding.itemsAnalysis.unavailableCount, 0);

  // Check if all items in all holdings are available
  const allItemsAvailable = totalUnavailableItems === 0;

  // Determine if deletion can proceed
  const canDeleteHoldings = totalItemsToDelete === 0; // No items across all holdings
  const canDeleteWithItems = allItemsAvailable; // All items are available
  const canProceed = canDeleteHoldings || canDeleteWithItems;

  const handleConfirm = useCallback(() => {
    onConfirm(selectedHoldings, canDeleteWithItems);
  }, [selectedHoldings, canDeleteWithItems, onConfirm]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      label={
        <FormattedMessage
          id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.title"
          defaultMessage="Delete Multiple Holdings"
        />
      }
      size="medium"
    >
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '20px' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.message"
            values={{
              holdingCount: selectedHoldings.length,
              itemCount: totalItemsToDelete,
            }}
            defaultMessage="Are you sure you want to delete {holdingCount} selected holdings containing {itemCount} items?"
          />
        </div>

        {/* Items Analysis Section */}
        {totalItemsToDelete > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '12px'
            }}
            >
              <h4 style={{ margin: '0 0 8px 0', color: '#0c4a6e' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.itemsAnalysis"
                  defaultMessage="Items Analysis Across All Holdings:"
                />
              </h4>
              <div style={{ fontSize: '14px', color: '#0c4a6e' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.availableItemsTotal"
                  values={{ count: totalAvailableItems }}
                  defaultMessage="• Total available items: {count}"
                />
                <br />
                <FormattedMessage
                  id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.unavailableItemsTotal"
                  values={{ count: totalUnavailableItems }}
                  defaultMessage="• Total unavailable items: {count}"
                />
              </div>
            </div>

            {/* Auto-delete notice or cannot delete */}
            {allItemsAvailable ? (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #fbbf24',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px'
              }}
              >
                <p style={{ margin: 0, fontWeight: 'bold', color: '#92400e' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.autoDeleteNotice"
                    defaultMessage="ℹ️ All items will be automatically deleted with these holdings"
                  />
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#92400e' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.allItemsAvailable"
                    defaultMessage="All items in these holdings are available and will be deleted automatically."
                  />
                </p>
              </div>
            ) : (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px'
              }}
              >
                <p style={{ margin: 0, fontWeight: 'bold', color: '#dc2626' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.cannotDeleteItems"
                    defaultMessage="⚠️ Cannot delete holdings: {unavailableCount} items are not available"
                    values={{ unavailableCount: totalUnavailableItems }}
                  />
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#7f1d1d' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.itemsRequirement"
                    defaultMessage="All items must have 'Available' status before deletion can proceed."
                  />
                </p>
              </div>
            )}
          </div>
        )}

        {/* Holdings list */}
        <div style={{ marginBottom: '20px' }}>
          <Label>
            <FormattedMessage
              id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.holdingsList.title"
              defaultMessage="Holdings to be deleted:"
            />
          </Label>
          <div style={{
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid #ccc',
            padding: '12px',
            borderRadius: '4px',
            backgroundColor: '#fff5f5',
            marginTop: '8px'
          }}
          >
            {selectedHoldingsDetails.map((holding) => (
              <HoldingItem
                key={holding.id}
                holding={holding}
                controlledVocab={controlledVocab}
              />
            ))}
          </div>
        </div>

        {/* Final Warning */}
        <div style={{
          padding: '12px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fbbf24',
          borderRadius: '6px',
          marginBottom: '16px',
        }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ color: '#f59e0b', fontSize: '18px', fontWeight: 'bold' }}>⚠️</span>
            <div style={{ fontSize: '14px', color: '#92400e' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.finalWarning"
                values={{
                  holdingCount: selectedHoldings.length,
                  itemCount: totalItemsToDelete,
                }}
                defaultMessage="Warning: This action cannot be undone. {holdingCount} holdings and {itemCount} items will be permanently deleted from the system."
              />
            </div>
          </div>
        </div>
      </div>

      <ModalFooter>
        <Button
          buttonStyle="danger"
          onClick={handleConfirm}
          disabled={isProcessing || !canProceed}
          marginBottom0
        >
          {isProcessing ? (
            <FormattedMessage
              id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.deleting"
              defaultMessage="Deleting Holdings..."
            />
          ) : (
            <FormattedMessage
              id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.confirm"
              values={{ count: selectedHoldings.length }}
              defaultMessage={canDeleteWithItems ? `Delete ${selectedHoldings.length} Holdings & Items` : `Delete ${selectedHoldings.length} Holdings`}
            />
          )}
        </Button>
        <Button
          buttonStyle="default"
          onClick={onClose}
          disabled={isProcessing}
          marginBottom0
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.batchDeleteHoldingsModal.cancel"
            defaultMessage="Cancel"
          />
        </Button>
      </ModalFooter>
    </Modal>
  );
}

BatchDeleteHoldingsModal.propTypes = propTypes;

export default BatchDeleteHoldingsModal;

